// Ce qui tient la place des données pendant leur chargement. La structure de la page s'affiche aussitôt ; chaque
// donnée attendue y a son trait de construction, à sa place et à sa taille, comme le crayon d'un plan avant l'encre :
// quand elle arrive, rien ne bouge autour. Les traits se tracent une fois de gauche à droite, puis respirent.

import type { CSSProperties } from "react";

// Des longueurs fixes plutôt que tirées au hasard : le rendu du serveur et celui du navigateur restent identiques.
const LONGUEURS = [64, 48, 82, 57, 71, 44, 88, 60, 52, 76, 67, 40];

export function Trait({
  largeur,
  i = 0,
  className = "",
}: {
  largeur: number | string; // en % de la ligne, ou une longueur CSS
  i?: number; // le rang du trait : les traits se tracent l'un après l'autre
  className?: string;
}) {
  const style = {
    width: typeof largeur === "number" ? `${largeur}%` : largeur,
    "--i": i,
  } as CSSProperties;
  return <span className={`trait ${className}`} style={style} aria-hidden />;
}

// Un document en liste (le mémoire, l'analyse, le DCE) : son titre, une ligne d'aide, puis ses entrées.
export function SqueletteDocument({ libelle }: { libelle: string }) {
  return (
    <section className="vue squelette" aria-busy="true" aria-label={libelle}>
      <div className="liste-doc">
        <Trait className="trait-h2" largeur={38} />
        <Trait className="trait-petit" largeur={56} i={1} />
        <ul>
          {LONGUEURS.slice(0, 9).map((l, k) => (
            <li key={k} className={k % 4 === 0 ? "" : "niveau-3"}>
              <span className="pastille squelette-pastille" />
              <Trait largeur={l} i={k + 2} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// Le panneau de droite : un en-tête et l'arborescence.
export function SqueletteArbre() {
  return (
    <div className="arbre squelette" aria-hidden>
      <div className="arbre-entete">
        <Trait className="trait-gras" largeur={70} />
        <Trait className="trait-petit" largeur={45} i={1} />
      </div>
      <ul>
        {LONGUEURS.map((l, k) => (
          <li
            key={k}
            className={`arbre-ligne ${k % 3 === 0 ? "" : "niveau-3"}`}
          >
            <span className="pastille squelette-pastille" />
            <Trait largeur={l} i={k + 2} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// La liste des chats : trois fils, un titre et sa ligne de détail.
export function SqueletteFils() {
  return (
    <ul
      className="fils-liste squelette"
      aria-busy="true"
      aria-label="Chargement des conversations"
    >
      {[78, 62, 70].map((l, k) => (
        <li key={k} className="fil">
          <Trait largeur={l} i={k * 2} />
          <Trait className="trait-petit" largeur={40} i={k * 2 + 1} />
        </li>
      ))}
    </ul>
  );
}

// Le chat, tant que ChatKit n'est pas prêt : le compositeur à sa place, en bas.
export function SqueletteChat() {
  return (
    <div
      className="squelette squelette-chat"
      aria-busy="true"
      aria-label="Chargement du chat"
    >
      <div className="squelette-compositeur">
        <Trait largeur={42} />
      </div>
    </div>
  );
}
